import { ComparisonOperators, FilterBuilder } from "@/utils/filterBuilder"
import { CommerceProduct } from "@/app/types"

interface MakeFilterProps {
  minPrice: number | null
  maxPrice: number | null
  categories: string[]
  vendors: string[]
  colors: string[]
  rating?: number | null
}

export function composeFilters(filter: FilterBuilder, parsedSearchParams: MakeFilterProps, separator: string) {
  const filterConditions = [
    {
      predicate: parsedSearchParams.categories.length > 0,
      action: () => {
        parsedSearchParams.categories.forEach((category) => {
          const level = category.split(separator).length - 1
          filter.or().group((sub) => sub.in(`hierarchicalCategories.lvl${level}`, [category]))
        })
      },
    },
    {
      predicate: parsedSearchParams.vendors.length > 0,
      action: () => filter.and().group((sub) => sub.in("vendor", parsedSearchParams.vendors)),
    },
    {
      predicate: parsedSearchParams.colors.length > 0,
      action: () => filter.and().group((sub) => sub.in("flatOptions.Color", parsedSearchParams.colors)),
    },
    {
      predicate: !!parsedSearchParams.minPrice,
      action: () =>
        filter.and().where("minPrice", ComparisonOperators.GreaterThanOrEqual, parsedSearchParams.minPrice!),
    },
    {
      predicate: !!parsedSearchParams.maxPrice,
      action: () => filter.and().where("minPrice", ComparisonOperators.LessThanOrEqual, parsedSearchParams.maxPrice!),
    },
    {
      predicate: !!parsedSearchParams.rating,
      action: () => filter.and().where("avgRating", ComparisonOperators.GreaterThanOrEqual, parsedSearchParams.rating!),
    },
  ]

  filterConditions.forEach(({ predicate, action }) => predicate && action())

  return filter
}

export function transformCategoriesToDistribution(
  categories: Array<{
    id: string
    name: string
    subcategories: Array<{ id: string; name: string }>
  }>
): Record<string, any> {
  const result: Record<string, any> = {}

  const processCategory = (category: any, path: string[] = []) => {
    const currentPath = [...path, category.name]

    if (category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach((subcat: any) => {
        processCategory(subcat, currentPath)
      })
    }
  }

  categories.forEach((category) => {
    processCategory(category)
  })

  return result
}

// Helper function to generate f distributions
export function generateFacetDistributions(products: CommerceProduct[]) {
  const facetDistribution: Record<string, number> = {}
  const independentFacetDistribution: Record<string, Record<string, number>> = {}

  // Example: Generate distribution for categories
  products.forEach((product) => {
    product.categories.forEach((category: any) => {
      facetDistribution[category] = (facetDistribution[category] || 0) + 1
    })

    // Example: Generate independent distribution for price ranges
    const priceRange = getPriceRange(product.price)
    if (!independentFacetDistribution["priceRange"]) {
      independentFacetDistribution["priceRange"] = {}
    }
    independentFacetDistribution["priceRange"][priceRange] =
      (independentFacetDistribution["priceRange"][priceRange] || 0) + 1
  })

  return { facetDistribution, independentFacetDistribution }
}

// Helper function to determine price range
function getPriceRange(price: number): string {
  if (price < 50) return "$0–$50"
  if (price < 100) return "$50–$100"
  if (price < 200) return "$100–$200"
  return "$200+"
}
